/**
 * SEO and OpenGraph image utilities for PWGen-Rust website
 * Strategic image assignment for optimal social media sharing and SEO
 */

// Strategic image mappings for different page types
export const getSEOImage = (pageType, slug = null, contentType = 'blog') => {
  
  // Strategic images for specific pages
  const strategicImages = {
    // Main pages
    home: '/img/pwgen-social-card.png',
    download: '/img/pwgen-social-card.png',
    pricing: '/img/pwgen-social-card.png',
    about: '/img/KS-Social.png',
    contact: '/img/KS-Social.png',
    
    // Documentation default
    docs: '/img/KS-Logo-Full-Light.png',
    
    // Blog defaults by content type
    blog: '/img/pwgen-social-card.png',
    
    // Security/Tools pages
    tools: '/img/pwgen-social-card.png',
    security: '/img/pwgen-social-card.png',
  };

  // Blog post specific image mappings (using the numbered generic images strategically)
  const blogPostImages = {
    'pwgen-1.0-release': '/img/1.webp',
    'website-launch': '/img/9.webp',
    'pwgen-v1.2-optimized-release': '/img/10.webp',
    'kief-studio-strategic-investment': '/img/13.webp',
  };

  // Documentation section images
  const docSectionImages = {
    'getting-started': '/img/KS-Logo-Full-Light.png',
    'cli': '/img/KS-Logo-Full-Light.png',
    'security': '/img/KS-Logo-Full-Light.png',
    'user-guide': '/img/KS-Logo-Full-Light.png',
    'developers': '/img/KS-Logo-Full-Light.png',
    'tutorials': '/img/KS-Logo-Full-Light.png',
  };

  // Return appropriate image based on context
  if (pageType === 'blog' && slug && blogPostImages[slug]) {
    return blogPostImages[slug];
  }
  
  if (pageType === 'docs' && slug && docSectionImages[slug]) {
    return docSectionImages[slug];
  }
  
  if (strategicImages[pageType]) {
    return strategicImages[pageType];
  }
  
  // Fallback to main social card
  return '/img/pwgen-social-card.png';
};

/**
 * Generate comprehensive meta tags for SEO
 */
export const generateSEOTags = ({
  title,
  description,
  keywords = [],
  image = null,
  url = '',
  type = 'website',
  publishedTime = null,
  modifiedTime = null,
  author = 'Kief Studio',
  siteName = 'PWGen-Rust'
}) => {
  const baseUrl = 'https://pwgenrust.dev';
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
  const fullImageUrl = image ? (image.startsWith('http') ? image : `${baseUrl}${image}`) : `${baseUrl}/img/pwgen-social-card.png`;
  
  return {
    // Basic meta tags
    title: `${title} | ${siteName}`,
    description,
    keywords: keywords.join(', '),
    author: author,
    viewport: 'width=device-width, initial-scale=1',
    robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    googlebot: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    bingbot: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    'theme-color': '#1976d2',
    'application-name': siteName,
    'apple-mobile-web-app-capable': 'yes',
    'apple-mobile-web-app-status-bar-style': 'default',
    'apple-mobile-web-app-title': siteName,
    'format-detection': 'telephone=no',
    'mobile-web-app-capable': 'yes',
    
    // Open Graph tags
    'og:type': type,
    'og:title': title,
    'og:description': description,
    'og:image': fullImageUrl,
    'og:image:secure_url': fullImageUrl,
    'og:image:type': 'image/png',
    'og:image:alt': title,
    'og:image:width': '1200',
    'og:image:height': '630',
    'og:url': fullUrl,
    'og:site_name': siteName,
    'og:locale': 'en_US',
    
    // Twitter Card tags
    'twitter:card': 'summary_large_image',
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': fullImageUrl,
    'twitter:image:alt': title,
    'twitter:site': '@Kief_ma',
    'twitter:creator': '@Kief_ma',
    'twitter:domain': 'pwgenrust.dev',
    
    // Additional meta tags
    'article:author': author,
    'article:published_time': publishedTime,
    'article:modified_time': modifiedTime,
    'article:publisher': 'https://kief.studio',
    
    // Dublin Core meta tags
    'DC.title': title,
    'DC.creator': author,
    'DC.description': description,
    'DC.publisher': 'Kief Studio',
    'DC.rights': 'Copyright © 2025 Kief Studio',
    'DC.language': 'en-US',
    
    // Canonical URL
    canonical: fullUrl,
    
    // Structured data
    structuredData: {
      '@context': 'https://schema.org',
      '@type': type === 'article' ? 'Article' : 'WebPage',
      headline: title,
      description,
      image: fullImageUrl,
      url: fullUrl,
      author: {
        '@type': 'Organization',
        name: author,
        url: 'https://kief.studio',
        sameAs: [
          'https://twitter.com/Kief_ma',
          'https://github.com/KiefStudioMA',
          'https://linkedin.com/company/kief-studio'
        ]
      },
      publisher: {
        '@type': 'Organization',
        name: siteName,
        url: baseUrl,
        logo: {
          '@type': 'ImageObject',
          url: `${baseUrl}/img/KS-Logo-Full-Light.png`,
          width: 600,
          height: 60
        },
        sameAs: [
          'https://twitter.com/Kief_ma',
          'https://github.com/KiefStudioMA/PWGen-Rust'
        ]
      },
      ...(publishedTime && { datePublished: publishedTime }),
      ...(modifiedTime && { dateModified: modifiedTime }),
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': fullUrl
      }
    }
  };
};

/**
 * Apply SEO tags to document head
 */
export const applySEOTags = (seoData) => {
  // Set document title
  document.title = seoData.title;
  
  // Helper function to set or update meta tag
  const setMetaTag = (name, content, property = false) => {
    if (!content) return;
    
    const attribute = property ? 'property' : 'name';
    const selector = `meta[${attribute}="${name}"]`;
    
    let meta = document.querySelector(selector);
    if (meta) {
      meta.setAttribute('content', content);
    } else {
      meta = document.createElement('meta');
      meta.setAttribute(attribute, name);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    }
  };
  
  // Set basic meta tags
  setMetaTag('description', seoData.description);
  setMetaTag('keywords', seoData.keywords);
  setMetaTag('author', seoData.author);
  setMetaTag('viewport', seoData.viewport);
  setMetaTag('robots', seoData.robots);
  setMetaTag('googlebot', seoData.googlebot);
  setMetaTag('bingbot', seoData.bingbot);
  setMetaTag('theme-color', seoData['theme-color']);
  setMetaTag('application-name', seoData['application-name']);
  setMetaTag('apple-mobile-web-app-capable', seoData['apple-mobile-web-app-capable']);
  setMetaTag('apple-mobile-web-app-status-bar-style', seoData['apple-mobile-web-app-status-bar-style']);
  setMetaTag('apple-mobile-web-app-title', seoData['apple-mobile-web-app-title']);
  setMetaTag('format-detection', seoData['format-detection']);
  setMetaTag('mobile-web-app-capable', seoData['mobile-web-app-capable']);
  
  // Set Open Graph, Twitter, Article, and Dublin Core tags
  Object.entries(seoData).forEach(([key, value]) => {
    if (key.startsWith('og:')) {
      setMetaTag(key, value, true);
    }
    if (key.startsWith('twitter:')) {
      setMetaTag(key, value);
    }
    if (key.startsWith('article:')) {
      setMetaTag(key, value, true);
    }
    if (key.startsWith('DC.')) {
      setMetaTag(key, value);
    }
  });
  
  // Set canonical URL
  let canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.setAttribute('href', seoData.canonical);
  } else {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = seoData.canonical;
    document.head.appendChild(canonical);
  }
  
  // Set structured data
  let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
  if (structuredDataScript) {
    structuredDataScript.remove();
  }
  
  structuredDataScript = document.createElement('script');
  structuredDataScript.type = 'application/ld+json';
  structuredDataScript.textContent = JSON.stringify(seoData.structuredData);
  document.head.appendChild(structuredDataScript);
};

/**
 * Page-specific SEO configurations
 */
export const pageSEOConfig = {
  home: {
    title: 'PWGen-Rust - Secure Password Manager Built in Rust',
    description: 'PWGen-Rust is a memory-safe, zero-knowledge password manager built in Rust. AES-256 encryption, cross-platform support, and open-source transparency.',
    keywords: ['password manager', 'Rust security', 'zero-knowledge', 'AES-256', 'open source', 'cross-platform'],
    image: '/img/pwgen-social-card.png'
  },
  
  download: {
    title: 'Download PWGen-Rust - Free Secure Password Manager',
    description: 'Download PWGen-Rust for Windows, macOS, and Linux. Free, open-source password manager with military-grade encryption and zero-knowledge architecture.',
    keywords: ['download PWGen-Rust', 'password manager download', 'Windows', 'macOS', 'Linux', 'free download'],
    image: '/img/pwgen-social-card.png'
  },
  
  tools: {
    title: 'Free Password Generator - No Ads, No Nonsense',
    description: 'Generate secure passwords, analyze password strength, create passphrases, and generate cryptographic hashes. Free online security tools with no ads, no registration, and no tracking.',
    keywords: [
      'free password generator',
      'secure password generator', 
      'password strength analyzer',
      'passphrase generator',
      'hash generator',
      'online security tools',
      'no ads password generator',
      'cryptographic hash',
      'SHA-256 generator',
      'MD5 generator',
      'random password generator',
      'strong password creator',
      'password security tool',
      'diceware passphrase',
      'password manager tools',
      'client side password generator',
      'privacy focused security tools',
      'offline password generator'
    ],
    image: '/img/pwgen-social-card.png'
  },
  
  blog: {
    title: 'PWGen-Rust Blog - Security News and Updates',
    description: 'Latest news, updates, and security insights from the PWGen-Rust team. Learn about password security, Rust development, and cybersecurity best practices.',
    keywords: ['PWGen-Rust blog', 'security news', 'password security', 'Rust development', 'cybersecurity'],
    image: '/img/pwgen-social-card.png'
  },
  
  docs: {
    title: 'PWGen-Rust Documentation - Complete User Guide',
    description: 'Comprehensive documentation for PWGen-Rust including installation, usage, CLI reference, security architecture, and developer guides.',
    keywords: ['PWGen-Rust documentation', 'user guide', 'CLI reference', 'installation guide', 'security'],
    image: '/img/KS-Logo-Full-Light.png'
  },
  
  about: {
    title: 'About Kief Studio - AI Integration & Technology Consulting',
    description: 'Kief Studio provides AI integration and technology consulting services, developing PWGen-Rust and other cybersecurity solutions.',
    keywords: ['Kief Studio', 'AI integration', 'technology consulting', 'cybersecurity', 'software development'],
    image: '/img/KS-Social.png'
  }
};

const seoUtils = {
  getSEOImage,
  generateSEOTags,
  applySEOTags,
  pageSEOConfig
};

export default seoUtils;